import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  BarChart3, ZoomIn, ZoomOut, AlertTriangle, Plus, Pencil, Trash2
} from 'lucide-react';
import {
  format, addDays, addWeeks, addMonths, differenceInDays,
  startOfDay, startOfQuarter, startOfYear,
  eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval,
  isToday, addQuarters, addYears
} from 'date-fns';
import { es } from 'date-fns/locale';

const ZOOM_LEVELS = [
  { key: 'dia', label: 'Dia', colWidth: 36, headerFormat: (d) => format(d, 'd'), groupFormat: (d) => format(d, 'MMM yyyy', { locale: es }), getColumns: (start, end) => eachDayOfInterval({ start, end }), daySpan: 1 },
  { key: 'semana', label: 'Semana', colWidth: 80, headerFormat: (d) => `S${format(d, 'w')}`, groupFormat: (d) => format(d, 'MMM yyyy', { locale: es }), getColumns: (start, end) => eachWeekOfInterval({ start, end }, { weekStartsOn: 1 }), daySpan: 7 },
  { key: 'mes', label: 'Mes', colWidth: 100, headerFormat: (d) => format(d, 'MMM', { locale: es }), groupFormat: (d) => format(d, 'yyyy'), getColumns: (start, end) => eachMonthOfInterval({ start, end }), daySpan: 30 },
  { key: 'trimestre', label: 'Trimestre', colWidth: 120, headerFormat: (d) => `T${Math.ceil((d.getMonth() + 1) / 3)}`, groupFormat: (d) => format(d, 'yyyy'), getColumns: (start, end) => { const cols = []; let c = startOfQuarter(start); while (c <= end) { cols.push(c); c = addQuarters(c, 1); } return cols; }, daySpan: 90 },
  { key: 'anio', label: 'A\u00f1o', colWidth: 140, headerFormat: (d) => format(d, 'yyyy'), groupFormat: () => '', getColumns: (start, end) => { const cols = []; let c = startOfYear(start); while (c <= end) { cols.push(c); c = addYears(c, 1); } return cols; }, daySpan: 365 },
];

const ARROW_COLORS = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#3B82F6', label: 'Azul' },
  { value: '#10b981', label: 'Verde' },
  { value: '#f59e0b', label: 'Naranja' },
  { value: '#ef4444', label: 'Rojo' },
  { value: '#8b5cf6', label: 'Violeta' },
  { value: '#64748b', label: 'Gris' },
  { value: '#000000', label: 'Negro' },
];

const statusColors = {
  pendiente: '#94a3b8',
  en_progreso: '#f59e0b',
  completada: '#10b981',
  bloqueada: '#ef4444',
};

export function GanttView({ tasks, project, onEditTask, onUpdateTaskDates, onAddSubtask, onDeleteTask, isJefatura, dependencies = [], criticalPath = [] }) {
  const [zoomIdx, setZoomIdx] = useState(0);
  const scrollRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [arrowsBehind, setArrowsBehind] = useState(false);
  const [arrowColor, setArrowColor] = useState('#6366f1');
  const [dragging, setDragging] = useState(null);
  const zoom = ZOOM_LEVELS[zoomIdx];
  const ROW_H = 36;
  const criticalSet = useMemo(() => new Set(criticalPath), [criticalPath]);

  // Calculate dynamic label width based on longest task name
  const LABEL_W = useMemo(() => {
    if (!tasks.length) return 220;
    const maxLen = Math.max(...tasks.map(t => {
      const prefix = (t.code ? `${t.code} ` : '');
      const indent = (t._depth || 0) * 2;
      return prefix.length + t.name.length + indent;
    }));
    return Math.max(220, Math.min(450, maxLen * 7.5 + 60));
  }, [tasks]);

  // Parse date-only to avoid timezone shifts (use noon to prevent DST issues)
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const d = String(dateStr).split('T')[0];
    return new Date(d + 'T12:00:00');
  };

  const { timelineStart, timelineEnd, columns } = useMemo(() => {
    const now = new Date();
    let minDate = project?.start_date ? parseDate(project.start_date) : now;
    let maxDate = project?.end_date ? parseDate(project.end_date) : addMonths(now, 3);
    tasks.forEach(t => {
      if (t.start_date) { const d = parseDate(t.start_date); if (d < minDate) minDate = d; }
      if (t.end_date) { const d = parseDate(t.end_date); if (d > maxDate) maxDate = d; }
    });
    const padStart = zoom.key === 'dia' ? addDays(minDate, -3) : zoom.key === 'semana' ? addWeeks(minDate, -1) : addMonths(minDate, -1);
    const padEnd = zoom.key === 'dia' ? addDays(maxDate, 7) : zoom.key === 'semana' ? addWeeks(maxDate, 2) : addMonths(maxDate, 2);
    const ts = startOfDay(padStart);
    const te = startOfDay(padEnd);
    return { timelineStart: ts, timelineEnd: te, columns: zoom.getColumns(ts, te) };
  }, [tasks, project, zoom]);

  const totalDays = differenceInDays(timelineEnd, timelineStart) || 1;
  const totalWidth = columns.length * zoom.colWidth;
  const pxPerDay = zoom.colWidth / zoom.daySpan;

  const getBarPosition = useCallback((task) => {
    if (!task.start_date || !task.end_date) return null;
    const s = startOfDay(parseDate(task.start_date));
    const e = startOfDay(parseDate(task.end_date));
    const dayOffset = differenceInDays(s, timelineStart);
    const left = Math.max(0, Math.round(dayOffset * pxPerDay));
    const duration = Math.max(1, differenceInDays(e, s) + 1);
    const width = Math.max(pxPerDay, Math.round(duration * pxPerDay));
    return { left, width };
  }, [timelineStart, pxPerDay]);

  const todayOffset = Math.round(differenceInDays(startOfDay(new Date()), timelineStart) * pxPerDay);

  // Build task index for arrow drawing
  const taskIndex = useMemo(() => {
    const idx = {};
    tasks.forEach((t, i) => { idx[t.id] = i; });
    return idx;
  }, [tasks]);

  // Compute arrow paths
  const arrows = useMemo(() => {
    if (!dependencies?.length) return [];
    return dependencies.map(dep => {
      const fromIdx = taskIndex[dep.from_task_id];
      const toIdx = taskIndex[dep.to_task_id];
      if (fromIdx === undefined || toIdx === undefined) return null;

      const fromTask = tasks[fromIdx];
      const toTask = tasks[toIdx];
      const fromPos = getBarPosition(fromTask);
      const toPos = getBarPosition(toTask);
      if (!fromPos || !toPos) return null;

      // Arrow: from right edge of 'from' bar to left edge of 'to' bar
      const fromX = fromPos.left + fromPos.width;
      const fromY = fromIdx * ROW_H + ROW_H / 2;
      const toX = toPos.left;
      const toY = toIdx * ROW_H + ROW_H / 2;

      return { dep, fromX, fromY, toX, toY, fromIdx, toIdx };
    }).filter(Boolean);
  }, [dependencies, taskIndex, tasks, getBarPosition]);

  // Drag handlers for moving task bars
  const didDragRef = useRef(false);

  const handleDragStart = useCallback((e, task) => {
    if (!isJefatura) return;
    e.stopPropagation();
    e.preventDefault();
    didDragRef.current = false;
    const startX = e.clientX;
    const startY = e.clientY;
    // Use date-only strings to avoid timezone shifts
    const origStartStr = task.start_date.split('T')[0];
    const origEndStr = task.end_date.split('T')[0];
    const origStart = new Date(origStartStr + 'T12:00:00');
    const origEnd = new Date(origEndStr + 'T12:00:00');
    setDragging({ taskId: task.id, startX, origStart, origEnd, currentDelta: 0, mouseX: e.clientX, mouseY: e.clientY });

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      if (Math.abs(dx) > 3) didDragRef.current = true;
      const daysDelta = Math.round(dx / pxPerDay);
      const newStart = addDays(origStart, daysDelta);
      const newEnd = addDays(origEnd, daysDelta);
      setDragging(prev => prev ? { ...prev, currentDelta: dx, mouseX: ev.clientX, mouseY: ev.clientY, newStart, newEnd } : null);
    };
    const onUp = (ev) => {
      const dx = ev.clientX - startX;
      const daysDelta = Math.round(dx / pxPerDay);
      if (daysDelta !== 0 && onUpdateTaskDates && didDragRef.current) {
        const newStart = addDays(origStart, daysDelta);
        const newEnd = addDays(origEnd, daysDelta);
        onUpdateTaskDates(task.id, newStart, newEnd);
      }
      setDragging(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [isJefatura, pxPerDay, onUpdateTaskDates]);

  // Group columns by parent header
  const groupedHeaders = useMemo(() => {
    if (zoom.key === 'anio') return null;
    const groups = [];
    let currentGroup = null;
    columns.forEach(col => {
      const gLabel = zoom.groupFormat(col);
      if (!currentGroup || currentGroup.label !== gLabel) {
        currentGroup = { label: gLabel, count: 1 };
        groups.push(currentGroup);
      } else {
        currentGroup.count++;
      }
    });
    return groups;
  }, [columns, zoom]);

  const renderArrowPath = (a) => {
    const cornerR = 4;
    const sx = a.fromX;
    const sy = a.fromY;
    const ex = a.toX;
    const ey = a.toY;

    let path;
    if (sx + 20 < ex) {
      // Simple route: go right, then down/up, then right
      const midX = (sx + ex) / 2;
      path = `M${sx},${sy} H${midX - cornerR}`;
      if (sy < ey) {
        path += ` Q${midX},${sy} ${midX},${sy + cornerR} V${ey - cornerR} Q${midX},${ey} ${midX + cornerR},${ey}`;
      } else if (sy > ey) {
        path += ` Q${midX},${sy} ${midX},${sy - cornerR} V${ey + cornerR} Q${midX},${ey} ${midX + cornerR},${ey}`;
      }
      path += ` H${ex}`;
    } else {
      // Complex route: go right, down around, left, down, right
      const detourX = Math.max(sx, ex) + 20;
      const midY = (sy + ey) / 2;
      path = `M${sx},${sy} H${detourX}`;
      path += ` V${midY}`;
      path += ` H${Math.min(sx, ex) - 20}`;
      path += ` V${ey}`;
      path += ` H${ex}`;
    }

    return path;
  };

  return (
    <Card className="border-gray-200 bg-white shadow-sm" data-testid="gantt-view">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2 text-gray-800">
            <BarChart3 className="w-5 h-5 text-[#7FD02E]" />
            Diagrama de Gantt
            {dependencies.length > 0 && (
              <span className="text-xs font-normal text-gray-400 ml-2" data-testid="dep-count">{dependencies.length} dep.</span>
            )}
            {criticalPath.length > 0 && (
              <span className="flex items-center gap-1 text-xs font-normal text-red-500 ml-2" data-testid="critical-path-indicator">
                <AlertTriangle className="w-3.5 h-3.5" />
                Camino critico ({criticalPath.length})
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Arrow controls */}
            {dependencies.length > 0 && (
              <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1" data-testid="arrow-controls">
                <label className="flex items-center gap-1.5 cursor-pointer" data-testid="arrows-behind-checkbox">
                  <input
                    type="checkbox"
                    checked={arrowsBehind}
                    onChange={(e) => setArrowsBehind(e.target.checked)}
                    className="rounded border-gray-300 text-[#7FD02E] focus:ring-[#7FD02E] w-3.5 h-3.5"
                  />
                  <span className="text-xs text-gray-500">Lineas atras</span>
                </label>
                <div className="w-px h-4 bg-gray-200" />
                <div className="flex items-center gap-1">
                  {ARROW_COLORS.map(c => (
                    <button
                      key={c.value}
                      title={c.label}
                      className={`w-4 h-4 rounded-full border-2 transition-all ${arrowColor === c.value ? 'border-gray-800 scale-125' : 'border-transparent hover:border-gray-300'}`}
                      style={{ backgroundColor: c.value }}
                      onClick={() => setArrowColor(c.value)}
                      data-testid={`arrow-color-${c.label.toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* Zoom controls */}
            <div className="flex items-center gap-1" data-testid="gantt-zoom-controls">
            <Button
              variant="ghost" size="icon" className="h-8 w-8"
              disabled={zoomIdx === 0}
              onClick={() => setZoomIdx(i => Math.max(0, i - 1))}
              data-testid="gantt-zoom-in"
            ><ZoomIn className="w-4 h-4" /></Button>
            <div className="flex bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
              {ZOOM_LEVELS.map((z, i) => (
                <button
                  key={z.key}
                  onClick={() => setZoomIdx(i)}
                  className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                    zoomIdx === i ? 'bg-white text-[#7FD02E] shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`gantt-zoom-${z.key}`}
                >
                  {z.label}
                </button>
              ))}
            </div>
            <Button
              variant="ghost" size="icon" className="h-8 w-8"
              disabled={zoomIdx === ZOOM_LEVELS.length - 1}
              onClick={() => setZoomIdx(i => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
              data-testid="gantt-zoom-out"
            ><ZoomOut className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {tasks.length === 0 ? (
          <div className="text-center py-12 text-gray-400">Sin tareas</div>
        ) : (
          <div className="flex border-t border-gray-200">
            {/* Left labels */}
            <div className="flex-shrink-0 border-r border-gray-200" style={{ width: LABEL_W }}>
              <div className="h-[50px] border-b border-gray-200 bg-gray-50 flex items-end px-3 pb-1">
                <span className="text-xs font-medium text-gray-500">Tarea</span>
              </div>
              {tasks.map((t) => (
                <div
                  key={t.id}
                  className={`border-b border-gray-100 flex items-center px-3 cursor-pointer hover:bg-gray-50 transition-colors group ${criticalSet.has(t.id) ? 'bg-red-50/50' : ''}`}
                  style={{ height: ROW_H, paddingLeft: 12 + (t._depth || 0) * 16 }}
                  onClick={() => onEditTask?.(t)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({ x: e.clientX, y: e.clientY, task: t });
                  }}
                  data-testid={`gantt-label-${t.id}`}
                >
                  {criticalSet.has(t.id) && <AlertTriangle className="w-3 h-3 text-red-400 mr-1 shrink-0" />}
                  <span className={`text-xs flex-1 group-hover:text-[#7FD02E] whitespace-nowrap ${criticalSet.has(t.id) ? 'text-red-600 font-medium' : 'text-gray-700'}`}>{t.code ? `${t.code} ` : ''}{t.name}</span>
                  {t.dependencies_in?.length > 0 && (
                    <span className="text-[9px] text-gray-400 ml-1 shrink-0" title="Tiene dependencias">{t.dependencies_in.length}dep</span>
                  )}
                  {isJefatura && (
                    <button
                      className="hidden group-hover:flex items-center justify-center w-5 h-5 rounded bg-[#7FD02E] hover:bg-[#6BC024] text-white ml-1 shrink-0 transition-all"
                      onClick={(e) => { e.stopPropagation(); onAddSubtask?.(t.id); }}
                      title="Agregar subtarea"
                      data-testid={`gantt-add-subtask-${t.id}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Right: scrollable timeline */}
            <div className="flex-1 overflow-x-auto" ref={scrollRef}>
              <div style={{ width: totalWidth, minWidth: '100%' }}>
                {/* Header */}
                <div className="border-b border-gray-200 bg-gray-50" style={{ height: 50 }}>
                  {groupedHeaders && (
                    <div className="flex h-[24px] border-b border-gray-100">
                      {groupedHeaders.map((g, i) => (
                        <div key={i} className="text-[10px] font-semibold text-gray-500 text-center uppercase tracking-wider border-r border-gray-100 flex items-center justify-center"
                          style={{ width: g.count * zoom.colWidth }}
                        >{g.label}</div>
                      ))}
                    </div>
                  )}
                  <div className="flex" style={{ height: groupedHeaders ? 26 : 50 }}>
                    {columns.map((col, i) => (
                      <div key={i}
                        className={`text-[10px] text-center flex items-center justify-center border-r border-gray-100 ${
                          zoom.key === 'dia' && isToday(col) ? 'bg-[#7FD02E]/10 font-bold text-[#7FD02E]' : 'text-gray-500'
                        }`}
                        style={{ width: zoom.colWidth }}
                      >
                        {zoom.headerFormat(col)}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Rows + arrows */}
                <div className="relative" style={{ height: tasks.length * ROW_H }}>
                  {/* Grid lines - ALWAYS at the very bottom */}
                  <div className="absolute inset-0 z-0" style={{ pointerEvents: 'none' }}>
                    {/* Vertical column lines */}
                    <div className="absolute inset-0 flex">
                      {columns.map((_, i) => (
                        <div key={i} className="border-r border-gray-100/60" style={{ width: zoom.colWidth, height: '100%' }} />
                      ))}
                    </div>
                    {/* Horizontal row lines */}
                    {tasks.map((_, i) => (
                      <div key={i} className="absolute w-full border-b border-gray-100/60" style={{ top: (i + 1) * ROW_H }} />
                    ))}
                  </div>

                  {/* Today line */}
                  {todayOffset > 0 && todayOffset < totalWidth && (
                    <div className="absolute top-0 bottom-0 w-0.5 bg-red-400 z-30" style={{ left: todayOffset }} />
                  )}

                  {/* Dependency arrows (SVG overlay) */}
                  {arrows.length > 0 && (
                    <svg
                      className={`absolute inset-0 pointer-events-none ${arrowsBehind ? 'z-[5]' : 'z-20'}`}
                      width={totalWidth}
                      height={tasks.length * ROW_H}
                      data-testid="gantt-dependency-arrows"
                    >
                      <defs>
                        <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                          <polygon points="0 0, 8 3, 0 6" fill={arrowColor} />
                        </marker>
                      </defs>
                      {arrows.map((a, i) => (
                        <path
                          key={a.dep.id || i}
                          d={renderArrowPath(a)}
                          fill="none"
                          stroke={arrowColor}
                          strokeWidth="1.5"
                          markerEnd="url(#arrowhead)"
                          opacity="0.7"
                          data-testid={`dep-arrow-${a.dep.id}`}
                        />
                      ))}
                    </svg>
                  )}

                  {/* Task bars */}
                  {tasks.map((t, rowIdx) => {
                    const pos = getBarPosition(t);
                    const isCritical = criticalSet.has(t.id);
                    const isDragging = dragging?.taskId === t.id;
                    const dragOffset = isDragging ? dragging.currentDelta : 0;
                    return (
                      <div key={t.id} className="absolute w-full z-10" style={{ top: rowIdx * ROW_H, height: ROW_H }}>
                        {pos && (
                          <div
                            className={`absolute top-1 rounded flex items-center overflow-hidden select-none ${isCritical ? 'ring-2 ring-red-400 ring-offset-1' : ''} ${isJefatura ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${isDragging ? 'opacity-80 shadow-lg' : 'hover:opacity-80'}`}
                            style={{
                              left: pos.left + dragOffset,
                              width: pos.width,
                              height: ROW_H - 8,
                              backgroundColor: isCritical ? '#dc2626' : (t.color || statusColors[t.status] || '#94a3b8'),
                              transition: isDragging ? 'none' : 'opacity 0.15s',
                            }}
                            onMouseDown={(e) => isJefatura ? handleDragStart(e, t) : undefined}
                            onClick={() => { if (!didDragRef.current) onEditTask?.(t); }}
                            data-testid={`gantt-bar-${t.id}`}
                          >
                            {t.progress > 0 && (
                              <div className="absolute inset-0 bg-black/15 rounded" style={{ width: `${t.progress}%` }} />
                            )}
                            {pos.width > 50 && (
                              <span className="text-[10px] text-white font-medium px-1.5 truncate relative z-10">{t.name}</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* Drag date tooltip */}
      {dragging && dragging.newStart && (
        <div
          className="fixed z-50 pointer-events-none bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-xl"
          style={{ left: dragging.mouseX + 12, top: dragging.mouseY - 36 }}
          data-testid="drag-date-tooltip"
        >
          {format(dragging.newStart, 'dd/MM/yyyy')} → {format(dragging.newEnd, 'dd/MM/yyyy')}
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div className="fixed inset-0 z-50" onClick={() => setContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}>
          <div
            className="absolute bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px] animate-in fade-in zoom-in-95 duration-100"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={(e) => e.stopPropagation()}
            data-testid="gantt-context-menu"
          >
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => { onEditTask?.(contextMenu.task); setContextMenu(null); }}
              data-testid="ctx-edit-task"
            >
              <Pencil className="w-3.5 h-3.5 text-gray-400" />
              Editar tarea
            </button>
            {isJefatura && (
              <>
                <button
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#7FD02E] hover:bg-[#7FD02E]/5 transition-colors"
                  onClick={() => { onAddSubtask?.(contextMenu.task.id); setContextMenu(null); }}
                  data-testid="ctx-add-subtask"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Agregar subtarea
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                  onClick={() => { onDeleteTask?.(contextMenu.task.id); setContextMenu(null); }}
                  data-testid="ctx-delete-task"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Eliminar tarea
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
